import { useCallback, useRef } from 'react';
import { useActionSpan } from '@repo/shared-kit/hooks/use-action-span';
import { type KycProvider } from '@repo/api-kit/queries/due-diligence/kyc';
import { isResponseError } from '@repo/api-kit/queries/utils';
import { useMaybeKycContext } from '../context/kyc-context';

interface UseKycFlowSpanParams {
  membershipId?: string;
  userId?: string;
}

export interface UseKycFlowSpanReturn {
  markSuccess: (
    provider: keyof typeof KycProvider | 'onfido',
    outcome?: 'completed' | 'skipped'
  ) => void;
  markError: (error: unknown) => void;
}

export function useKycFlowSpan(overrides?: UseKycFlowSpanParams): UseKycFlowSpanReturn {
  const context = useMaybeKycContext();
  const membershipId = overrides?.membershipId ?? context?.membershipId;
  const userId = overrides?.userId ?? context?.userId;
  const origin = context?.trackingEventOrigin;

  const outcomeRef = useRef<'completed' | 'skipped' | undefined>(undefined);
  const providerRef = useRef<string | undefined>(undefined);
  const errorAttrsRef = useRef<Record<string, string>>({});

  const { markSuccess: _markSuccess, markError: _markError } = useActionSpan('kyc.flow', {
    attributes: {
      domain: window.location.hostname,
      origin,
      membership_id: membershipId,
      user_id: userId,
      get outcome() {
        return outcomeRef.current;
      },
      get provider() {
        return providerRef.current;
      },
      get 'error.status'() {
        return errorAttrsRef.current['error.status'];
      },
      get 'error.url'() {
        return errorAttrsRef.current['error.url'];
      },
      get 'error.status_text'() {
        return errorAttrsRef.current['error.status_text'];
      },
      get 'error.data'() {
        return errorAttrsRef.current['error.data'];
      },
    },
  });

  const markSuccess = useCallback(
    (
      provider: keyof typeof KycProvider | 'onfido',
      outcome: 'completed' | 'skipped' = 'completed'
    ) => {
      providerRef.current = String(provider);
      outcomeRef.current = outcome;
      _markSuccess();
    },
    [_markSuccess]
  );

  const markError = useCallback(
    (error: unknown) => {
      if (isResponseError(error)) {
        errorAttrsRef.current = {
          'error.status': String(error.status),
          'error.url': error.request.url,
          'error.status_text': error.response.statusText,
          'error.data': JSON.stringify(error.data),
        };
      }
      _markError(error);
    },
    [_markError]
  );

  return { markSuccess, markError };
}
